import { Flex } from "../primitives/Flex";
import { styled } from "@pigment-css/react";
import { DateRangeSelector } from "../DateRangeSelector";
import type { DateRange, PeriodOption } from "../DateRangeSelector/types";

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  dateRangeLabel?: string | null;
  showDateRangeSelector?: boolean;
  selectedPeriod?: PeriodOption;
  customDateRange?: DateRange;
  onPeriodChange?: (period: PeriodOption) => void;
  onCustomDateRangeChange?: (dateRange: DateRange) => void;
  excludedPeriods?: PeriodOption[];
  actionButton?: React.ReactNode;
}

const Title = styled("h1")(({ theme }) => ({
  fontFamily: theme.managementView.font.family,
  fontSize: theme.managementView.font.size[21],
  fontWeight: theme.managementView.font.weight[500],
  letterSpacing: theme.managementView.font.letterSpacing.tight,
  color: theme.managementView.colors.neutral[90],
  margin: 0,
  padding: 0,
}));

const Subtitle = styled("p")(({ theme }) => ({
  fontFamily: theme.managementView.font.family,
  fontSize: theme.managementView.font.size[16],
  fontWeight: theme.managementView.font.weight[500],
  letterSpacing: theme.managementView.font.letterSpacing.tight,
  color: theme.managementView.colors.neutral[56],
  margin: 0,
  padding: 0,
  marginTop: theme.managementView.spacing[4],
}));

const DateRangeLabel = styled("p")(({ theme }) => ({
  fontFamily: theme.managementView.font.family,
  fontSize: theme.managementView.font.size[13],
  fontWeight: theme.managementView.font.weight[500],
  letterSpacing: theme.managementView.font.letterSpacing.tight,
  color: theme.managementView.colors.neutral[56],
  margin: 0,
  padding: 0,
}));

const PageHeaderContainer = styled(Flex)({
  width: "100%",
});

export const PageHeader = ({
  title,
  subtitle,
  dateRangeLabel,
  showDateRangeSelector = false,
  selectedPeriod,
  customDateRange,
  onPeriodChange,
  onCustomDateRangeChange,
  excludedPeriods,
  actionButton,
}: PageHeaderProps) => {
  const showSelector = showDateRangeSelector && selectedPeriod && onPeriodChange;

  return (
    <PageHeaderContainer justify="between" align="center">
      <Flex direction="column">
        <Title>{title}</Title>
        {subtitle && <Subtitle>{subtitle}</Subtitle>}
        {dateRangeLabel && <DateRangeLabel>{dateRangeLabel}</DateRangeLabel>}
      </Flex>
      <Flex align="center" gap={10}>
        {showSelector && (
          <DateRangeSelector
            selectedPeriod={selectedPeriod}
            customDateRange={customDateRange}
            onPeriodChange={onPeriodChange}
            onCustomDateRangeChange={onCustomDateRangeChange}
            excludedPeriods={excludedPeriods}
          />
        )}
        {actionButton}
      </Flex>
    </PageHeaderContainer>
  );
};
